<template>
  <div class="profile-search-results">
    <div
      v-if="loading"
      class="d-flex justify-center pa-4"
    >
      <v-progress-circular
        indeterminate
        color="brandPrimary"
        size="32"
        width="2"
      />
    </div>

    <div
      v-else-if="results.length === 0 && hasSearched"
      class="text-body-2 text-textLight text-center pa-4"
    >
      No matching profiles found.
    </div>

    <div
      v-else-if="results.length === 0"
      class="text-body-2 text-textLight text-center pa-4"
    >
      Start typing to search for existing profiles.
    </div>

    <v-list
      v-else
      class="pa-0"
    >
      <v-list-item
        v-for="result in results"
        :key="result.ulid"
        color="brandPrimary"
        rounded="lg"
        class="profile-search-result-item pa-3 ma-2"
        :class="{ 'border rounded-lg border-primary': selectedUlid === result.ulid }"
        @click="$emit('select', selectedUlid === result.ulid ? null : result)"
      >
        <v-list-item-title class="text-body-1 text-textDarkest">
          {{ result.title }}
        </v-list-item-title>

        <v-list-item-subtitle>
          <JobProfileMeta :profile="result" />
        </v-list-item-subtitle>

        <v-list-item-subtitle
          class="mt-2 d-flex flex-wrap ga-2 justify-space-between"
        >
          <div class="d-flex align-center">
            <JobProfileStatus
              :is-hris-profile="result.is_hris_profile"
              :status="result.status"
            />
          </div>

          <div
            v-if="selectedUlid === result.ulid"
            class="d-flex ga-2"
            @click.stop
          >
            <v-btn
              variant="outlined"
              color="primary"
              size="small"
              rounded="lg"
              :to="{
                name: result.is_hris_profile ? 'job-profile-hris-view' : 'job-profile-view',
                params: { id: result.ulid },
              }"
            >
              View in Editor
            </v-btn>
            <v-btn
              v-if="result.is_hris_profile"
              variant="outlined"
              color="primary"
              size="small"
              rounded="lg"
              @click="duplicatingResult = result"
            >
              Duplicate Profile
            </v-btn>
          </div>
        </v-list-item-subtitle>
      </v-list-item>
    </v-list>

    <Modal
      :show="!!duplicatingResult"
      action-text="Duplicate"
      width="small"
      :disabled="duplicating"
      @action="duplicateProfile"
      @close="duplicatingResult = null"
    >
      <template #title>
        Duplicate Profile
      </template>
      <template #content>
        An exact copy of <strong>{{ duplicatingResult?.title }}</strong> profile will be created.
      </template>
    </Modal>
  </div>
</template>

<script setup>
import { ref } from 'vue';
import { useRouter } from 'vue-router';
import JobProfileStatus from '@/components/jobProfiles/editor/JobProfileStatus.vue';
import JobProfileMeta from '@/components/jobProfiles/editor/JobProfileMeta.vue';
import Modal from '@/components/shared/Modal.vue';
import { duplicateJobProfile } from '@/api/jobProfile';
import { useAppSnackbar } from '@/stores/app/snackbar';

defineProps({
  results: {
    type: Array,
    default: () => [],
  },
  loading: {
    type: Boolean,
    default: false,
  },
  selectedUlid: {
    type: String,
    default: null,
  },
  hasSearched: {
    type: Boolean,
    default: false,
  },
});

defineEmits(['select']);

const router = useRouter();
const { addErrorSnackbar } = useAppSnackbar();

const duplicatingResult = ref(null);
const duplicating = ref(false);

const duplicateProfile = async () => {
  duplicating.value = true;
  try {
    const { document_ulid: documentUlid } = await duplicateJobProfile({ ulid: duplicatingResult.value.ulid });
    duplicatingResult.value = null;
    router.push({ name: 'job-profile-view', params: { id: documentUlid } });
  } catch (error) {
    addErrorSnackbar({ text: `Failed to duplicate profile - ${error.message}` });
    throw error;
  } finally {
    duplicating.value = false;
  }
};
</script>
